import { RealtimeEventHandler } from './event_handler.js';
import { RealtimeUtils } from './utils.js';

export class RealtimeAPI extends RealtimeEventHandler {
  /**
   * Create a new RealtimeAPI instance
   * @param {{url?: string, apiKey?: string, dangerouslyAllowAPIKeyInBrowser?: boolean, debug?: boolean}} [settings]
   * @returns {RealtimeAPI}
   */
  constructor({ url, apiKey, dangerouslyAllowAPIKeyInBrowser, debug } = {}) {
    super();
    this.defaultUrl = 'wss://api.openai.com/v1/realtime';
    this.url = url || this.defaultUrl;
    this.apiKey = apiKey || null;
    this.debug = !!debug;
    this.ws = null;
    if (globalThis.document && this.apiKey) {
      if (!dangerouslyAllowAPIKeyInBrowser) {
        throw new Error(
          `Can not provide API key in the browser without "dangerouslyAllowAPIKeyInBrowser" set to true`,
        );
      }
    }
  }

  /**
   * Tells us whether or not the WebSocket is connected
   * @returns {boolean}
   */
  isConnected() {
    return !!this.ws;
  }

  /**
   * Writes WebSocket logs to console
   * @param  {...any} args
   * @returns {true}
   */
  log(...args) {
    const date = new Date().toISOString();
    const logs = [`[Websocket/${date}]`].concat(args).map((arg) => {
      if (typeof arg === 'object' && arg !== null) {
        return JSON.stringify(arg, null, 2);
      } else {
        return arg;
      }
    });
    if (this.debug) {
      console.log(...logs);
    }
    return true;
  }

  /**
   * Connects to Realtime API Websocket Server
   * @param {{model?: string}} [settings]
   * @returns {Promise<true>}
   */
  async connect({ model } = { model: 'gpt-4o-realtime-preview-2024-10-01' }) {
    if (!this.apiKey && this.url === this.defaultUrl) {
      console.warn(`No apiKey provided for connection to "${this.url}"`);
    }
    if (this.isConnected()) {
      throw new Error(`Already connected`);
    }
    if (globalThis.document) {
      /**
       * Web browser
       */
      if (this.apiKey) {
        console.warn(
          'Warning: Connecting using API key in the browser, this is not recommended',
        );
      }
      const WebSocket = globalThis.WebSocket;
      console.log('this.url', this.url);
      // if the url already has parameters, append them, otherwise add the model parameter  
      const wsUrl = this.url.includes('?') ? `${this.url}&model=${model}` : `${this.url}?model=${model}`;
      const ws = new WebSocket(wsUrl, [
        'realtime',
        `openai-insecure-api-key.${this.apiKey}`,
        'openai-beta.realtime-v1',
      ]);
      return new Promise((resolve, reject) => {
        const connectionErrorHandler = (error) => {
          this.disconnect(ws);
          reject(error);
        };
        
        const connectionTimeoutId = setTimeout(() => {
          connectionErrorHandler(new Error('Connection timeout'));
        }, 1000); // 1 seconds timeout

        ws.addEventListener('error', (event) => connectionErrorHandler(new Error(event?.message || 'WebSocket error')));
        ws.addEventListener('message', (event) => {
          //  console.log('event:', event);
          const message = JSON.parse(event.data);
          if (message.error) {
            clearTimeout(connectionTimeoutId);
            connectionErrorHandler(new Error(message.error));
          } else if (message.type === 'connection.authorized') {
            clearTimeout(connectionTimeoutId);
            console.log(`Connected and authorized to "${this.url}"`);
            ws.removeEventListener('error', connectionErrorHandler);
            ws.addEventListener('error', () => {
              this.disconnect(ws);
              console.log(`Error, disconnected from "${this.url}"`);
              this.dispatch('close', { error: true });
            });
            ws.addEventListener('close', () => {
              this.disconnect(ws);
              console.log(`Disconnected from "${this.url}"`);
              this.dispatch('close', { error: false });
            });
            this.ws = ws;
            resolve(true);
          } else {
              this.receive(message.type, message);
          }
        });
        ws.addEventListener('open', () => {
          console.log(`WebSocket opened to "${this.url}", waiting for authorization...`);
        });
      });
    } else {
      /**
       * Node.js
       */
      const moduleName = 'ws';
      const wsModule = await import(/* webpackIgnore: true */ moduleName);
      const WebSocket = wsModule.default;
      const wsUrl = `${this.url}?model=${model}`;
      const ws = new WebSocket(wsUrl, [], {
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'OpenAI-Beta': 'realtime=v1',
        },
      });

      return new Promise((resolve, reject) => {
        const connectionErrorHandler = (error) => {
          this.disconnect(ws);
          reject(error);
        };

        ws.on('error', (error) => {
          connectionErrorHandler(new Error(`WebSocket error: ${error.message}`));
        });

        ws.on('open', () => {
          this.log(`WebSocket opened to "${this.url}"`);
          this.ws = ws;

          // Set up message handler
          ws.on('message', (data) => {
            const message = JSON.parse(data.toString());
            if (message.error) {
              connectionErrorHandler(new Error(message.error));
            } else {
              this.receive(message.type, message);
            }
          });

          // Set up error and close handlers
          ws.on('error', () => {
            this.disconnect(ws);
            this.log(`Error, disconnected from "${this.url}"`);
            this.dispatch('close', { error: true });
          });
          ws.on('close', () => {
            this.disconnect(ws);
            this.log(`Disconnected from "${this.url}"`);
            this.dispatch('close', { error: false });
          });

          resolve(true);
        });
      });
    }
  }

  /**
   * Disconnects from Realtime API server
   * @param {WebSocket} [ws]
   * @returns {true}
   */
  disconnect(ws) {
    if (!ws || this.ws === ws) {
      this.ws && this.ws.close();
      this.ws = null;
      return true;
    }
  }

  /**
   * Receives an event from WebSocket and dispatches as "server.{eventName}" and "server.*" events
   * @param {string} eventName
   * @param {{[key: string]: any}} event
   * @returns {true}
   */
  receive(eventName, event) {
    this.log(`received:`, eventName, event);
    this.dispatch(`server.${eventName}`, event);
    this.dispatch('server.*', event);
    return true;
  }

  /**
   * Sends an event to WebSocket and dispatches as "client.{eventName}" and "client.*" events
   * @param {string} eventName
   * @param {{[key: string]: any}} event
   * @returns {true}
   */
  send(eventName, data) {
    if (!this.isConnected()) {
      throw new Error(`RealtimeAPI is not connected`);
    }
    data = data || {};
    if (typeof data !== 'object') {
      throw new Error(`data must be an object`);
    }
    const event = {
      event_id: RealtimeUtils.generateId('evt_'),
      type: eventName,
      ...data,
    };
    this.dispatch(`client.${eventName}`, event);
    this.dispatch('client.*', event);
    this.log(`sent:`, eventName, event);
    this.ws.send(JSON.stringify(event));
    return true;
  }
}
